<template>
  <div v-if="loading" class="history-side-panel__empty">
    <div class="loading"></div>
  </div>
  <!-- Before the empty state, not after it. A failed fetch also leaves the
       list empty, so without this the user is told there are no runs when in
       fact we could not find out. -->
  <div v-else-if="error" class="history-side-panel__empty">
    <Alert type="error">
      <template #title>{{ $t('historySidePanel.errorTitle') }}</template>
      {{ $t('historySidePanel.errorDescription') }}
      <template #actions>
        <Button type="secondary" size="small" @click="refreshData">
          {{ $t('historySidePanel.retry') }}
        </Button>
      </template>
    </Alert>
  </div>
  <div
    v-else-if="!workflowHistoryItems.length"
    class="history-side-panel__empty"
  >
    <Icon
      class="history-side-panel__empty-icon"
      icon="baserow-icon-automation"
      type="secondary"
    />
    <h4>{{ $t('historySidePanel.noRunsTitle') }}</h4>
    <p class="margin-top-0">
      {{ $t('historySidePanel.noRunsDescription') }}
    </p>
  </div>
  <div v-else>
    <div class="history-side-panel__title">
      <span>
        {{ $t('historySidePanel.title') }}
        <Icon icon="iconoir-refresh" type="secondary" @click="refreshData()" />
      </span>

      <a role="button" @click="closeHistory()">
        <Icon icon="iconoir-cancel" type="secondary" />
      </a>
    </div>

    <div class="history-side-panel__divider"></div>

    <div class="history-side-panel__counts">
      <div class="history-side-panel__counts-runs">
        <div class="history-side-panel__counts-runs-label">
          {{ $t('historySidePanel.successfulRuns') }}
        </div>
        <div class="history-side-panel__counts-runs-total">
          {{ history.success_count }}
        </div>
      </div>
      <div class="history-side-panel__counts-runs">
        <div class="history-side-panel__counts-runs-label">
          {{ $t('historySidePanel.failedRuns') }}
        </div>
        <div class="history-side-panel__counts-runs-total">
          {{ history.fail_count }}
        </div>
      </div>
    </div>

    <WorkflowHistory
      v-for="item in workflowHistoryItems"
      :key="item.id"
      :item="item"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import WorkflowHistory from '@baserow/modules/automation/components/workflow/sidePanels/WorkflowHistory'
const store = useStore()

const workflow = inject('workflow')

const loading = ref(false)
const error = ref(false)

const history = computed(() => {
  return store.getters['automationHistory/getWorkflowHistory']()
})

const workflowHistoryItems = computed(() => {
  return history.value?.results || []
})

const refreshData = async () => {
  loading.value = true
  error.value = false
  try {
    store.dispatch('automationHistory/invalidate')
    await store.dispatch('automationHistory/fetchWorkflowHistory', {
      workflowId: workflow.value.id,
    })
  } catch (e) {
    // This was a bare `try`/`finally`: the spinner stopped and the panel fell
    // through to its empty state, so a failed fetch was indistinguishable from
    // a workflow that has never run. The user was told there was no history
    // rather than that we could not load it, and had nothing to retry with.
    error.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  refreshData()
})

const closeHistory = () => {
  store.dispatch('automationWorkflow/setActiveSidePanel', null)
}
</script>
